import React from 'react';
import {numberClass} from 'components/manager-dashboard/Common';
import classnames from 'classnames';
import {BarChartIcon} from '@components/icon/BarChartIcon';
import TrendChartContainer from 'components/manager-dashboard/strengths-and-weaknesses/TrendChartContainer';
import Popper from '@material-ui/core/Popper';
import TableScore from 'components/manager-dashboard/strengths-and-weaknesses/TableScore';
import connectToStores from 'alt-utils/lib/connectToStores';
import AuthStore from 'stores/AuthStore';
import {UserConditionType} from 'records/UserCondition';
import FeedbackTrigger, {
  FeedbackMatchAllContext,
  FeedbackMatchAnyContext,
  FeedbackRecipientContext,
  FeedbackTriggerScoreContext,
} from 'records/FeedbackTrigger';
import {browserHistory} from 'react-router';
import round from 'lodash/round';
import Button from '@material-ui/core/Button';
import AddNotificationIcon from 'components/pure/utils/icon/AddNotificationIcon';
import User from 'records/User';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

@connectToStores
export default class TableCell extends React.Component {
  constructor(props) {
    super(props);

    this.filterTooltipData = this.filterTooltipData.bind(this);
    this.goToSmartAlerts = this.goToSmartAlerts.bind(this);
    this.onPopoverRequestClose = this.onPopoverRequestClose.bind(this);
    this.openPopover = this.openPopover.bind(this);

    const {state} = props.location;
    const openPopover = state
      ? state.openCellPopupUserId === props.item.userId && state.openCellPopupSkill === props.skill
      : false;
    if (openPopover) {
      // Clear the location state so that going back and forth doesn't keep opening the popup.
      browserHistory.replace(this.props.location.pathname);
    }
    this.state = {
      openPopover,
    };
  }

  static getStores() {
    return [AuthStore];
  }

  static getPropsFromStores() {
    return {
      sessionUser: AuthStore.getState().user,
    };
  }

  /**
   * Redirect to the triggers editor. We'll fill in a reasonable feedback trigger suggestion based on
   * the rep's chart data.
   */
  goToSmartAlerts(userId, skill, score) {
    const recipient = UserConditionType.getUserCondition(UserConditionType.USER, userId);
    const rule = new FeedbackTrigger({
      user: [UserConditionType.getUserCondition(UserConditionType.ANY_USER)],
      context: new FeedbackMatchAllContext({
        matchAll: [
          new FeedbackMatchAllContext({
            matchAll: [
              new FeedbackTriggerScoreContext({range: {start: 0, end: score}}),
              new FeedbackMatchAnyContext({
                matchAny: [new FeedbackRecipientContext({recipient})],
              }),
            ],
          }),
        ],
      }),
    });
    const {location} = this.props;
    // Return to this page, with the popup for this cell open.
    const comeBack = () => {
      browserHistory.push({
        pathname: location.pathname,
        state: {
          openCellPopupUserId: userId,
          openCellPopupSkill: skill,
        },
      });
    };

    browserHistory.push({
      pathname: '/admin/actions/create',
      state: {rule, cancelNav: comeBack},
    });
  }

  onPopoverRequestClose() {
    this.setState({
      openPopover: false,
    });
  }

  openPopover() {
    this.setState({openPopover: true});
  }

  barsClick(e, item) {
    e.stopPropagation();
    e.preventDefault();
    if (this.props.rowTooltip) {
      if (this.props.rowTooltip(item)) {
        this.openPopover();
      }
    }
  }

  filterTooltipData(data) {
    return data.filter(d => d.label === this.props.skill);
  }

  repButtons(score) {
    // Only render buttons for rep strengths and weaknesses (not for teams).
    if (!this.props.isRep) {
      return null;
    }
    const {item, onCreatePlan, sessionUser, skill} = this.props;
    const {userId} = item;
    const createPlan = () => {
      // popover sits on top of the wizard, so close it first.
      this.onPopoverRequestClose();
      const coach = sessionUser;
      const rep = new User(item);
      onCreatePlan({
        coach,
        rep,
        skillName: skill,
        // Open the popover again when closing the dialog, canceling the plan's creation. Creating
        // the plan takes you to coaching plans page.
        onHideDialog: this.openPopover,
      });
    };
    // score is 0-5. Scale to percentage for triggers. Round it (just as is done by TableScore)
    // so the user sees the same value in both places.
    const skillScore = score && round(score * 20);
    return (
      <div className="rep-buttons">
        <Button
          variant="outlined"
          className="ev-button"
          onClick={() => this.goToSmartAlerts(userId, skill, skillScore || 0)}>
          <div className="label">
            <AddNotificationIcon />
            <span>Create Smart Alert</span>
          </div>
        </Button>
        {sessionUser.id !== item.userId && ( // You can't create a plan for yourself.
          <Button variant="contained" color="primary" className="ev-button" onClick={createPlan}>
            Create Coaching Plan
          </Button>
        )}
      </div>
    );
  }

  render() {
    const {scores, skill, item, hovered} = this.props;
    const scoreObj = scores.skillScores[skill];
    let score, className;
    if (scoreObj !== undefined) {
      score = scoreObj['skillAverage'];
      className = numberClass(score);
    } else {
      className = 'not-applicable';
    }
    return (
      <td
        key={`${item.id}-${skill}`}
        className={classnames(className, 'skill', hovered ? 'hovered' : null)}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        ref={c => (this.popoverAnchor = c)}>
        <TableScore {...{score}} />
        <div className={'bars-icon cell'} onClick={e => this.barsClick(e, item)}>
          <BarChartIcon tooltip="Over time chart" />
        </div>
        {this.state.openPopover && (
          <ClickAwayListener onClickAway={this.onPopoverRequestClose}>
            <Popper
              open
              anchorEl={this.popoverAnchor}
              anchorOrigin={{horizontal: 'middle', vertical: 'top'}}
              targetOrigin={{horizontal: 'middle', vertical: 'bottom'}}
              className="skill-popover destyle-popover">
              <div className="tooltip-container">
                <div className="ev-tooltip">
                  <div className="tooltip-content">
                    <TrendChartContainer
                      chartLabel={`Score Trend for '${skill}' by '${item.displayName}'`}
                      chartButtons={this.repButtons(score)}
                      filterData={this.filterTooltipData}
                      onClose={this.onPopoverRequestClose}
                      legend={null}
                    />
                  </div>
                </div>
              </div>
            </Popper>
          </ClickAwayListener>
        )}
      </td>
    );
  }
}
